import dayjs from "dayjs";
import dayjsTimezone from "dayjs/plugin/timezone";
import quarter from "dayjs/plugin/quarterOfYear";
import dayjsUTC from "dayjs/plugin/utc";
import dayjsIsSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(dayjsIsSameOrAfter)
dayjs.extend(dayjsUTC)
dayjs.extend(quarter);
dayjs.extend(dayjsTimezone);

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
};

export function utcTimestamp() {
  return Math.floor((new Date()).getTime() / 1000);
};

export function dayjsInPacificTZ(date?: Date | number | string, keepLocalTime?: boolean) {
  return dayjs(date).tz('America/Los_Angeles', keepLocalTime);
};

export function dayjsInUTC(date?: Date | number | string) {
  return dayjs(date).utc();
};

export const startOfToday = dayjs().startOf('day');
export const endOfToday = startOfToday.endOf('day');

export interface DateRangeValue {
  label: string;
  from: Date;
  to?: Date;
};

export const DATE_RANGE_PRESETS: DateRangeValue[] = [
  { label: "Today", from: startOfToday.toDate(), to: endOfToday.toDate() },
  { label: "Yesterday", from: startOfToday.subtract(1, "day").toDate(), to: startOfToday.subtract(1, "day").endOf('day').toDate() },
  { label: "Last month", from: startOfToday.subtract(1, "month").startOf('month').startOf('day').toDate(), to: startOfToday.subtract(1, 'month').endOf('month').endOf('day').toDate() },
  { label: "Past 7 days", from: startOfToday.subtract(7, "day").startOf('day').toDate(), to: startOfToday.subtract(1, "day").endOf('day').toDate() },
  { label: "Past 30 days", from: startOfToday.subtract(30, "day").startOf('day').toDate(), to: startOfToday.subtract(1, "day").endOf('day').toDate() },
  { label: "Past 60 days", from: startOfToday.subtract(60, "day").startOf('day').toDate(), to: startOfToday.subtract(1, "day").endOf('day').toDate() },
  { label: "Past 90 days", from: startOfToday.subtract(90, "day").startOf('day').toDate(), to: startOfToday.subtract(1, "day").endOf('day').toDate() },
  { label: "Month to date", from: startOfToday.startOf("month").startOf('day').toDate(), to: endOfToday.toDate() },
  { label: "Quarter to date", from: startOfToday.startOf("quarter").startOf('day').toDate(), to: endOfToday.toDate() },
  { label: "Year to date", from: startOfToday.startOf("year").startOf('day').toDate(), to: endOfToday.toDate() },
];

export const isSameDay = (date1: Date | number | string, date2: Date | number | string) => {
  return dayjs(date1).isSame(date2, 'day');
};

export const isSameDayOrAfter = (date1: Date | number | string, date2: Date | number | string) => {
  return dayjs(date1).isSameOrAfter(date2, 'day');
};

export const isAfterNoe = (date: Date | number | string) => {
  return dayjs(date).isAfter(dayjs().startOf('day'));
};

export const getLocalDateAsUTCDate = (date: Date | number | string, shift: 'startOfDay' | 'endOfDay') => {
  return new Date(`${dayjs(date).format('YYYY-MM-DD')}${shift === 'startOfDay' ? 'T00:00:00.000Z' : 'T23:59:59.999Z'}`)
};

export const getUTCDateAsLocalDate = (date: Date | number | string) => {
  const dateISO = new Date(date).toISOString()
  const dateWithoutTime = dateISO.split('T')[0]
  const time = dateISO.split('T')[1].replace('Z', '')
  return new Date(`${dateWithoutTime}T${time}`)
};

export const formatISODate = (isoDate: string | number | Date)  => {
  if (!isoDate) {
    return "--";
  }

  const date = isoDate instanceof Date ? isoDate : new Date(isoDate);

  // Days of the week and months of the year
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Get day, month, and date
  const month = months[date.getMonth()];
  const dateNumber = date.getDate();

  // Add suffix for the day (e.g., 1st, 2nd, 3rd, etc.)
  const suffix = (() => {
    if (dateNumber % 10 === 1 && dateNumber !== 11) return "st";
    if (dateNumber % 10 === 2 && dateNumber !== 12) return "nd";
    if (dateNumber % 10 === 3 && dateNumber !== 13) return "rd";
    return "th";
  })();

  // Get hours, minutes, and format in 12-hour time
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12; // Convert to 12-hour format
  const formattedMinutes = minutes.toString().padStart(2, "0");

  // Combine the parts into the desired format
  return `${month} ${dateNumber}${suffix} at ${hours}:${formattedMinutes}${ampm}`;
};


export const formatUnixTimestampToObject = (isoDate: number)  => {
  const date = new Date(isoDate);

  // Days of the week and months of the year
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Get day, month, and date
  const month = months[date.getMonth()];
  const dateNumber = date.getDate();

  // Add suffix for the day (e.g., 1st, 2nd, 3rd, etc.)
  const suffix = (() => {
    if (dateNumber % 10 === 1 && dateNumber !== 11) return "st";
    if (dateNumber % 10 === 2 && dateNumber !== 12) return "nd";
    if (dateNumber % 10 === 3 && dateNumber !== 13) return "rd";
    return "th";
  })();

  // Get hours, minutes, and format in 12-hour time
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const year = date.getFullYear().toString().slice(-2);
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12; // Convert to 12-hour format
  const formattedMinutes = minutes.toString().padStart(2, "0");

  // Combine the parts into the desired format
  const monthNumber = date.getMonth() + 1

  return {
    month,
    dateNumber,
    suffix,
    hours,
    minutes,
    ampm,
    formattedMinutes,
    year,
    monthNumber
  }
};

export const getDifferenceInMinutes = (date1: string, date2: string) => {
  // Parse the ISO strings into Date objects
  const dateObj1 = new Date(date1);
  const dateObj2 = new Date(date2);

  // Calculate the difference in milliseconds
  const diffInMillis = Math.abs(dateObj1.getTime() - dateObj2.getTime());

  // Convert milliseconds to minutes
  const diffInMinutes = Math.ceil(diffInMillis / (1000 * 60));

  return diffInMinutes;
};